import { httpsCallable, type HttpsCallable } from 'firebase/functions';
import { z } from 'zod';

import { functions } from '../firebase.js';
import { SchemaValidationFailedError } from '@kilat/shared';

/**
 * Typed wrapper around the `askKilat` Cloud Function (Sprint 72 — AI
 * Foundation). Operator dashboard's "Ask Kilat" assistant.
 */

export const AskKilatInputSchema = z.object({
  question: z.string().min(2).max(1000),
  tenantId: z.string().min(1).optional(),
});
export type AskKilatInput = z.infer<typeof AskKilatInputSchema>;

export const AskKilatOutputSchema = z.object({
  text: z.string(),
  cacheHit: z.boolean(),
  /** 'together' / 'groq' / 'fireworks' / 'noop' — UI uses 'noop' to
   *  show a "AI not configured yet" hint instead of the stub text. */
  provider: z.string(),
});
export type AskKilatOutput = z.infer<typeof AskKilatOutputSchema>;

const askKilatFn: HttpsCallable<AskKilatInput, AskKilatOutput> = httpsCallable(
  functions,
  'askKilat',
);

export async function callAskKilat(input: AskKilatInput): Promise<AskKilatOutput> {
  const parsed = AskKilatInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('askKilat.input', parsed.error.flatten());
  }
  const result = await askKilatFn(parsed.data);
  const out = AskKilatOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('askKilat.output', out.error.flatten());
  }
  return out.data;
}

// ── aiDraftPromotion (Sprint 75) ────────────────────────────────────────

export const AiDraftPromotionInputSchema = z.object({
  intent: z.string().min(4).max(400),
  tenantId: z.string().min(1),
});
export type AiDraftPromotionInput = z.infer<typeof AiDraftPromotionInputSchema>;

export const AiPromotionDraftSchema = z.object({
  name: z.string(),
  description: z.string(),
  templateKey: z.enum(['day_of_week', 'happy_hour', 'first_visit', 'always_on']),
  dows: z.array(z.number().int().min(0).max(6)),
  timeFrom: z.string().nullable().optional(),
  timeTo: z.string().nullable().optional(),
  effectType: z.enum(['pct_off', 'fixed_off']),
  effectValue: z.number().int().nonnegative(),
  validForDays: z.number().int().positive(),
});
export type AiPromotionDraft = z.infer<typeof AiPromotionDraftSchema>;

export const AiDraftPromotionOutputSchema = z.object({
  provider: z.string(),
  cacheHit: z.boolean(),
  draft: AiPromotionDraftSchema.nullable(),
  rawText: z.string(),
});
export type AiDraftPromotionOutput = z.infer<typeof AiDraftPromotionOutputSchema>;

const aiDraftPromotionFn: HttpsCallable<AiDraftPromotionInput, AiDraftPromotionOutput> =
  httpsCallable(functions, 'aiDraftPromotion');

export async function callAiDraftPromotion(
  input: AiDraftPromotionInput,
): Promise<AiDraftPromotionOutput> {
  const parsed = AiDraftPromotionInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('aiDraftPromotion.input', parsed.error.flatten());
  }
  const result = await aiDraftPromotionFn(parsed.data);
  const out = AiDraftPromotionOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('aiDraftPromotion.output', out.error.flatten());
  }
  return out.data;
}

// ── aiDraftBroadcast (Sprint 78) ────────────────────────────────────────

export const AiDraftBroadcastInputSchema = z.object({
  intent: z.string().min(4).max(400),
  tenantId: z.string().min(1),
});
export type AiDraftBroadcastInput = z.infer<typeof AiDraftBroadcastInputSchema>;

export const AiBroadcastDraftSchema = z.object({
  title: z.string(),
  body: z.string(),
  audienceKey: z.enum(['all', 'active_30d', 'silver_tier_min', 'gold_tier_min']),
  deepLink: z.string(),
  rationale: z.string(),
});
export type AiBroadcastDraft = z.infer<typeof AiBroadcastDraftSchema>;

export const AiDraftBroadcastOutputSchema = z.object({
  provider: z.string(),
  cacheHit: z.boolean(),
  draft: AiBroadcastDraftSchema.nullable(),
  rawText: z.string(),
});
export type AiDraftBroadcastOutput = z.infer<typeof AiDraftBroadcastOutputSchema>;

const aiDraftBroadcastFn: HttpsCallable<AiDraftBroadcastInput, AiDraftBroadcastOutput> =
  httpsCallable(functions, 'aiDraftBroadcast');

export async function callAiDraftBroadcast(
  input: AiDraftBroadcastInput,
): Promise<AiDraftBroadcastOutput> {
  const parsed = AiDraftBroadcastInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('aiDraftBroadcast.input', parsed.error.flatten());
  }
  const result = await aiDraftBroadcastFn(parsed.data);
  const out = AiDraftBroadcastOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('aiDraftBroadcast.output', out.error.flatten());
  }
  return out.data;
}

// ── retentionPulse (AI-tools Slice 2 — operator win-back nudge) ──────────
//
// Aggregate-only: the callable returns counts/sums (never a per-customer list)
// plus a THIN optional AI narrative that no-ops to `null` without a Groq key.

export const RetentionPulseInputSchema = z.object({
  tenantId: z.string().min(1),
  windowDays: z.number().int().positive().max(3650).optional(),
});
export type RetentionPulseInput = z.infer<typeof RetentionPulseInputSchema>;

export const RetentionPulseOutputSchema = z.object({
  windowDays: z.number().int().positive(),
  lapsedCount: z.number().int().nonnegative(),
  atRiskSpendSen: z.number().int().nonnegative(),
  /** True when the at-risk-spend sum was capped (more lapsed than the read cap). */
  truncated: z.boolean(),
  /** Optional AI narrative; `null` when AI is off / over-budget / errored / zero-lapsed. */
  narrative: z.string().nullable(),
  /** 'groq' / 'noop' / 'budget' / 'none' / 'error' — UI shows numbers + CTA regardless. */
  provider: z.string(),
});
export type RetentionPulseOutput = z.infer<typeof RetentionPulseOutputSchema>;

const retentionPulseFn: HttpsCallable<RetentionPulseInput, RetentionPulseOutput> = httpsCallable(
  functions,
  'retentionPulse',
);

export async function callRetentionPulse(
  input: RetentionPulseInput,
): Promise<RetentionPulseOutput> {
  const parsed = RetentionPulseInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('retentionPulse.input', parsed.error.flatten());
  }
  const result = await retentionPulseFn(parsed.data);
  const out = RetentionPulseOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('retentionPulse.output', out.error.flatten());
  }
  return out.data;
}

// ── aiRewriteShareCaption (Share & grow) ────────────────────────────────

export const AiRewriteShareCaptionInputSchema = z.object({
  tenantId: z.string().min(1),
  caption: z.string().trim().min(4).max(600),
  locale: z.enum(['en', 'ms', 'zh', 'ta']),
  channel: z.enum(['whatsapp', 'facebook', 'instagram', 'tiktok', 'x', 'telegram']),
});
export type AiRewriteShareCaptionInput = z.infer<typeof AiRewriteShareCaptionInputSchema>;

export const AiRewriteShareCaptionOutputSchema = z.object({
  /** 'noop' = no AI key configured; the UI says so instead of failing. */
  provider: z.string(),
  cacheHit: z.boolean(),
  caption: z.string().nullable(),
});
export type AiRewriteShareCaptionOutput = z.infer<typeof AiRewriteShareCaptionOutputSchema>;

const aiRewriteShareCaptionFn: HttpsCallable<
  AiRewriteShareCaptionInput,
  AiRewriteShareCaptionOutput
> = httpsCallable(functions, 'aiRewriteShareCaption');

export async function callAiRewriteShareCaption(
  input: AiRewriteShareCaptionInput,
): Promise<AiRewriteShareCaptionOutput> {
  const parsed = AiRewriteShareCaptionInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('aiRewriteShareCaption.input', parsed.error.flatten());
  }
  const result = await aiRewriteShareCaptionFn(parsed.data);
  const out = AiRewriteShareCaptionOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('aiRewriteShareCaption.output', out.error.flatten());
  }
  return out.data;
}
